import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useContext } from "react";
import {
  getInternalProjectsOfGeneration,
  getMandatoryWorkshopsOfGeneration,
  getMentorsOfGeneration,
  getTraineeChoicesOfGeneration,
  getTraineeMotivation,
  getTraineeProgress,
  setElectionDeadline as setElectionDeadlineApi,
  updateTraineePreferences,
  addMentor,
  removeMentor,
} from "../../api/generations";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { authReducerActionType } from "../../types/globalTypes";
import { MentorDto } from "../../types/membersTypes";
import {
  InternalProjectDto,
  MandatoryWorkshop,
  TraineeChoiceDto,
  TraineeMotivationDto,
  TraineeProgressOverviewDto,
} from "../../types/traineesTypes";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";

/**
 * Hook that handles the trainee generation api calls, uses react-query
 * @param generationID - The generation ID
 * @returns The trainee progress, a boolean indicating if the trainee progress is fetched, the trainee choices, the trainee motivation, the internal projects of the generation, a boolean indicating if the internal projects of the generation are fetched and a function to set the election deadline
 */
const useTraineeGeneration = (generationID: number) => {
  const { dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // getTraineeProgress query
  const { data: traineeProgressData, isFetched: isTraineeProgressFetched } = useQuery({
    queryKey: ["traineeProgress", generationID],
    queryFn: () => {
      if (typeof generationID === "number" && generationID > 0) {
        return getTraineeProgress(generationID);
      }
      return Promise.resolve(null);
    },
  });

  const traineeProgress: TraineeProgressOverviewDto[] = traineeProgressData?.data || [];

  // ----------------------------------------------------------------------------------
  // getMandatoryWorkshopsOfGeneration query
  const { data: mandatoryWorkshopsData } = useQuery({
    queryKey: ["mandatoryWorkshops", generationID],
    queryFn: () => {
      if (typeof generationID === "number" && generationID > 0) {
        return getMandatoryWorkshopsOfGeneration(generationID);
      }
      return Promise.resolve(null);
    },
  });

  const mandatoryWorkshops: MandatoryWorkshop[] = mandatoryWorkshopsData?.data || [];

  // ----------------------------------------------------------------------------------
  // getTraineeChoicesOfGeneration query
  const { data: traineeChoicesData } = useQuery({
    queryKey: ["traineeChoices", generationID],
    queryFn: () => {
      if (typeof generationID === "number" && generationID > 0) {
        return getTraineeChoicesOfGeneration(generationID);
      }
      return Promise.resolve(null);
    },
  });

  const traineeChoices: TraineeChoiceDto[] = traineeChoicesData?.data || [];

  // ----------------------------------------------------------------------------------
  // getTraineeMotivation query
  const { data: traineeMotivationData } = useQuery({
    queryKey: ["traineeMotivation", generationID],
    queryFn: () => {
      if (typeof generationID === "number" && generationID > 0) {
        return getTraineeMotivation(generationID);
      }
      return Promise.resolve(null);
    },
  });

  const traineeMotivation: TraineeMotivationDto | null = traineeMotivationData?.data || null;

  // ----------------------------------------------------------------------------------
  // getInternalProjectsOfGeneration query
  const { data: internalProjectsOfGenerationData, isFetched: isInternalProjectsOfGenerationFetched } = useQuery({
    queryKey: ["internalProjectsOfGeneration", generationID],
    queryFn: () => {
      if (typeof generationID === "number" && generationID > 0) {
        return getInternalProjectsOfGeneration(generationID);
      }
      return Promise.resolve(null);
    },
  });

  const internalProjectsOfGeneration: InternalProjectDto[] = internalProjectsOfGenerationData?.data || [];

  // ----------------------------------------------------------------------------------
  // getMentorsOfGeneration query
  const { data: mentorsOfGenerationData } = useQuery({
    queryKey: ["mentorsOfGeneration", generationID],
    queryFn: () => {
      if (typeof generationID === "number" && generationID > 0) {
        return getMentorsOfGeneration(generationID);
      }
      return Promise.resolve(null);
    },
  });

  const mentorsOfGeneration: MentorDto[] = mentorsOfGenerationData?.data || [];

  // ----------------------------------------------------------------------------------
  // getTraineePreferencesForGeneration query
  const { data: traineePreferencesOfGenerationData, isFetched: isTraineePreferencesOfGenerationFetched } = useQuery({
    queryKey: ["traineePreferencesOfGeneration", generationID],
    queryFn: () => {
      if (typeof generationID === "number" && generationID > 0) {
        return getTraineeChoicesOfGeneration(generationID);
      }
      return Promise.resolve(null);
    },
  });

  const traineePreferencesOfGeneration: TraineeChoiceDto[] = traineePreferencesOfGenerationData?.data || [];

  // ############
  // UPDATE QUERIES
  // ############

  // ----------------------------------------------------------------------------------
  // updateTraineePreferences mutation
  const { mutateAsync: updatePreferences } = useMutation({
    mutationFn: ({ memberId, preferences }: { memberId: number; preferences: TraineeChoiceDto }) =>
      updateTraineePreferences(memberId, preferences),
    onSuccess: async () => {
      showSuccessMessage("Deine Präferenzen wurden gespeichert.");
      await queryClient.invalidateQueries({ queryKey: ["traineePreferences", generationID] });
    },
    onError: (err: AxiosError) => {
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
      showErrorMessage("Präferenzen konnten nicht gespeichert werden!");
    },
  });

  // ----------------------------------------------------------------------------------
  // setElectionDeadline mutation
  const { mutateAsync: mutateSetElectionDeadline } = useMutation({
    mutationFn: setElectionDeadlineApi,
    onError: (err: AxiosError) => {
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
      showErrorMessage("Fehler beim Setzen des Wahlendes!");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["currentTraineeGeneration"] });
      showSuccessMessage("Wahlenddatum erfolgreich gesetzt!");
    },
  });

  /**
   * Sets the election deadline
   * @param electionDeadline - The election deadline
   * @returns The election deadline
   */
  const setElectionDeadline = async (electionStart: Date, electionEnd: Date) => {
    return await mutateSetElectionDeadline({
      generationId: generationID,
      electionStart: electionStart,
      electionEnd: electionEnd,
    });
  };

  // ----------------------------------------------------------------------------------
  // Add mentor to generation mutation

  const { mutateAsync: addMentorToGenerationMutation } = useMutation({
    mutationFn: ({ mentorId }: { mentorId: number }) => addMentor(generationID, mentorId),
    onError: (err: AxiosError) => {
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
      showErrorMessage("Fehler beim Hinzufügen des Mentors!");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mentorsOfGeneration", generationID] });
      showSuccessMessage("Mentor erfolgreich hinzugefügt!");
    },
  });

  /**
   * Adds a mentor to the generation
   * @param mentorId - The ID of the mentor to add
   * @returns void
   */
  const addMentorToGeneration = async (mentorId: number) => {
    return await addMentorToGenerationMutation({ mentorId });
  };

  // ############
  // DELETE QUERIES
  // ############

  // ----------------------------------------------------------------------------------
  // Remove mentor from generation mutation

  const { mutateAsync: removeMentorFromGenerationMutation } = useMutation({
    mutationFn: ({ mentorId }: { mentorId: number }) => removeMentor(generationID, mentorId),
    onError: (err: AxiosError) => {
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
      showErrorMessage("Fehler beim Entfernen des Mentors!");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mentorsOfGeneration", generationID] });
      showSuccessMessage("Mentor erfolgreich entfernt!");
    },
  });

  /**
   * Removes a mentor from the generation
   * @param mentorId - The ID of the mentor to remove
   * @return void
   */
  const removeMentorFromGeneration = async (mentorId: number) => {
    return await removeMentorFromGenerationMutation({ mentorId });
  };

  return {
    traineeProgress,
    isTraineeProgressFetched,
    mandatoryWorkshops,
    traineeChoices,
    traineeMotivation,
    internalProjectsOfGeneration,
    isInternalProjectsOfGenerationFetched,
    mentorsOfGeneration,
    traineePreferencesOfGeneration,
    isTraineePreferencesOfGenerationFetched,
    updatePreferences,
    setElectionDeadline,
    addMentorToGeneration,
    removeMentorFromGeneration,
  };
};

export default useTraineeGeneration;
